import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { StyledTextField, StyledSelect } from './StyledFormComponents';

declare global {
  interface Window {
    turnstile: any;
  }
}

const relationshipTypes = [
  'Artist/Creator',
  'Record Label',
  'Publisher',
  'Legal Representative/Attorney',
  'Rights Management Organization',
  'Other'
];

const CopyrightClaimForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    rights_holder_name: '',
    rights_holder_relationship: '',
    infringing_content_url: '',
    infringing_content_description: '',
    original_work_description: '',
    proof_of_ownership: '',
    additional_info: '',
  });

  // Initialize Turnstile verification
  useEffect(() => {
    let attemptInterval: ReturnType<typeof setTimeout>;

    const attemptRenderTurnstile = () => {
      if (window.turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        try {
          const widgetId = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: '0x4AAAAAABfgu43iKR6cfJY3',
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken('');
            },
            'error-callback': () => {
              setTurnstileToken('');
              setError('Verification challenge failed. Please try refreshing the page.');
            },
          });
          turnstileWidgetIdRef.current = widgetId;
          clearInterval(attemptInterval);
        } catch (renderError) {
          console.error('Turnstile render error:', renderError);
          setError('Could not load verification challenge. Please refresh.');
        }
      }
    };

    attemptRenderTurnstile();
    attemptInterval = setInterval(attemptRenderTurnstile, 1000);

    return () => {
      clearInterval(attemptInterval);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!turnstileToken) {
      setError('Please complete the human verification step.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/support/copyright-claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          cfTurnstileToken: turnstileToken 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong.');
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, mb: 2 }}>
          ✅ Copyright Claim Submitted
        </Typography>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', opacity: 0.9 }}>
          Thank you for submitting your copyright claim. We will review it and contact you via email within 5 business days.
        </Typography>
      </Box>
    );
  }

  const isButtonDisabled = loading || !turnstileToken;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, opacity: 0.8, fontFamily: '"Poppins", sans-serif' }}>
        If you believe your copyrighted work has been infringed on Lsnto, please complete this form with as much detail as possible.
      </Typography>

      <StyledTextField 
        label="Your Name" 
        required 
        value={form.name} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)} 
      />
      
      <StyledTextField 
        label="Your Email" 
        required 
        type="email" 
        value={form.email} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)} 
      />

      <StyledTextField 
        label="Rights Holder Name (if different from your name)" 
        value={form.rights_holder_name} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('rights_holder_name', e.target.value)} 
      />
      
      <StyledSelect
        label="Your Relationship to the Rights Holder"
        value={form.rights_holder_relationship}
        onChange={(e: React.ChangeEvent<{ value: unknown }>) => handleChange('rights_holder_relationship', e.target.value)}
        options={relationshipTypes}
      />

      <StyledTextField 
        label="URL of Infringing Content" 
        required 
        placeholder="https://lsnto.com/..." 
        value={form.infringing_content_url} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('infringing_content_url', e.target.value)} 
      />

      <StyledTextField 
        label="Description of Infringing Content" 
        multiline 
        rows={2}
        placeholder="Describe what content is infringing (track, album, artist name, etc.)"
        value={form.infringing_content_description} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('infringing_content_description', e.target.value)} 
      />

      <StyledTextField 
        label="Description of Your Original Work" 
        required
        multiline 
        rows={2}
        placeholder="Describe your original work that is being infringed"
        value={form.original_work_description} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('original_work_description', e.target.value)} 
      />

      <StyledTextField 
        label="Proof of Ownership (URL to registration, license, etc.)" 
        placeholder="https://..." 
        value={form.proof_of_ownership} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('proof_of_ownership', e.target.value)} 
      />

      <StyledTextField 
        label="Additional Information" 
        multiline 
        rows={3}
        placeholder="Any additional details that may help us process your claim"
        value={form.additional_info} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('additional_info', e.target.value)} 
      />

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <div ref={turnstileContainerRef} />
      </Box>

      {error && <Typography color="error" sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem' }}>{error}</Typography>}

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isButtonDisabled}
        sx={{ 
          mt: 1, 
          py: 1.5, 
          fontFamily: '"Poppins", sans-serif', 
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '0.95rem',
          boxShadow: '0 4px 12px rgba(255, 0, 84, 0.3)',
          background: 'linear-gradient(45deg, #FF0054, #662d91)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(255, 0, 84, 0.4)',
          }
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit Copyright Claim'}
      </Button>
    </Box>
  );
};

export default CopyrightClaimForm;






